import { DisabledOptionInfo } from 'components/dropdown/Dropdown';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { UpdateInterval } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { collectionIntervalExceedsUpdateInterval, OptionChild } from 'utils/node';

// Only Timed mode has a concrete, frontend-known interval to validate against.
export function getValidationUpdateIntervalMs(
    updateInterval: UpdateInterval,
): number | undefined {
    switch (updateInterval.mode) {
        case 'timed':
            return updateInterval.intervalMs;
        case 'smartsample':
        case 'triggered':
            return undefined;
        default:
            updateInterval satisfies never;
            return undefined;
    }
}

type CollectionDurationContext = Readonly<{
    samplingFrequencyHz: number | undefined;
    numberOfSamples: number | undefined;
    updateInterval: UpdateInterval;
}>;

export function collectionDurationExceedsInterval({
    samplingFrequencyHz,
    numberOfSamples,
    updateInterval,
}: CollectionDurationContext): boolean {
    return (
        collectionIntervalExceedsUpdateInterval({
            samplingFrequency: samplingFrequencyHz,
            numberOfSamples,
            updateInterval: getValidationUpdateIntervalMs(updateInterval),
        }) === true
    );
}

export function buildDisabledOptions(
    options: OptionChild<number>[],
    isValueInvalid: (value: number) => boolean,
    message: string,
): DisabledOptionInfo[] {
    return options
        .filter((option) => isValueInvalid(option.Value))
        .map((option) => ({ key: option.Value, message }));
}

// A single option can be invalid for more than one independent reason (e.g. a primary sample
// count that's both too long for the update interval AND leaves no room for Low-Frequency
// Collection). Dropdown resolves the tooltip via disabledItems.find(), which would otherwise
// silently show only the first rule's message and hide the other. Merging combines every
// applicable reason into one message per option instead of losing all but one.
export function mergeDisabledOptions(
    ...groups: DisabledOptionInfo[][]
): DisabledOptionInfo[] {
    const messagesByKey = new Map<DisabledOptionInfo['key'], string[]>();
    for (const group of groups) {
        for (const { key, message } of group) {
            const messages = messagesByKey.get(key) ?? [];
            if (!messages.includes(message)) {
                messages.push(message);
            }
            messagesByKey.set(key, messages);
        }
    }
    return Array.from(messagesByKey, ([key, messages]) => ({
        key,
        message: messages.join(' '),
    }));
}

export function getDisabledSamplingFrequencyOptions(
    options: OptionChild<number>[],
    {
        numberOfSamples,
        updateInterval,
    }: Omit<CollectionDurationContext, 'samplingFrequencyHz'>,
): DisabledOptionInfo[] {
    const intervalMs = getValidationUpdateIntervalMs(updateInterval);
    return buildDisabledOptions(
        options,
        (samplingFrequency) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        V4_SETTINGS_TEXT.validation.collectionDurationExceedsUpdateInterval,
    );
}

export function getDisabledNumberOfSamplesOptions(
    options: OptionChild<number>[],
    {
        samplingFrequencyHz,
        updateInterval,
    }: Omit<CollectionDurationContext, 'numberOfSamples'>,
): DisabledOptionInfo[] {
    const intervalMs = getValidationUpdateIntervalMs(updateInterval);
    return buildDisabledOptions(
        options,
        (numberOfSamples) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency: samplingFrequencyHz,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        V4_SETTINGS_TEXT.validation.collectionDurationExceedsUpdateInterval,
    );
}

export function getDisabledUpdateIntervalOptions(
    options: OptionChild<number>[],
    {
        samplingFrequencyHz,
        numberOfSamples,
    }: Pick<CollectionDurationContext, 'samplingFrequencyHz' | 'numberOfSamples'>,
): DisabledOptionInfo[] {
    return buildDisabledOptions(
        options,
        (intervalMs) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency: samplingFrequencyHz,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        V4_SETTINGS_TEXT.validation.collectionDurationExceedsThisInterval,
    );
}
